"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { useState, useEffect } from "react";
import { iConfirmBookingBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { convertHtmlToPlainText } from "@/utils/html-to-plain-text";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { CheckCircle } from "lucide-react";
import { QuoteBookingConfirmedDepositReminder } from "./quote-booking-confirmed-deposit-reminder";
import { QuoteBookingConfirmationOptions } from "./quote-booking-confirmation-options";

interface ConfirmBookingSectionProps extends iConfirmBookingBlockData {
  primaryColor?: string; // legacy, theme CSS vars used
}

export function ConfirmBookingSection({
  bookingBody,
  depositAmount,
  depositDueDate,
    ...blockData
}: ConfirmBookingSectionProps) {
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingTitle, setBookingTitle] = useState(
    "Ready to Secure Your Holiday"
  );

  const { quote } = useHolidayQuoteBrand();

  const alreadyBooked = quote.metadata?.status === "CONFIRMED";

  useEffect(() => {
    const timer = setTimeout(() => {
      setBookingTitle(
        convertHtmlToPlainText(bookingBody || "")
          ? bookingBody!
          : "Ready to Secure Your Holiday"
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [bookingBody]);

  if (bookingConfirmed || alreadyBooked) {
    return (
      <div
        className="bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-8 text-center space-y-4 sm:space-y-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
        style={quoteThemeColorStyles.accentBorder}
      >
        <div
          className="absolute top-0 right-0 text-white px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
          style={quoteThemeColorStyles.primaryBg}
        >
          <CheckCircle className="w-3 h-3" />
          Confirmed
        </div>

        <div className="flex justify-center relative z-10">
          <div
            className="rounded-full p-2 sm:p-3"
            style={quoteThemeColorStyles.primary10Bg}
          >
            <CheckCircle
              className="w-8 h-8 sm:w-12 sm:h-12"
              style={quoteThemeColorStyles.primary}
            />
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Booking Confirmed!
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-md mx-auto">
          Thank you for booking with us. We&apos;ll be in touch shortly with next
          steps and payment details.
        </p>
        <QuoteBookingConfirmedDepositReminder
          quote={quote}
          depositAmount={depositAmount}
          depositDueDate={depositDueDate}

        />
        <p className="text-xs sm:text-sm text-gray-500">
          If you have any questions, please don&apos;t hesitate to contact our
          support team.
        </p>
      </div>
    );
  }

  return (
    <section
      className="bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-6 space-y-4 sm:space-y-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
      style={quoteThemeColorStyles.accentBorder}
    >
      <div
        className="absolute top-0 right-0 text-white px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
        style={quoteThemeColorStyles.primaryBg}
      >
        <CheckCircle className="w-3 h-3" />
        Continue Booking
      </div>

      <div className="space-y-4 sm:space-y-6 relative z-10">
        <div className="text-center">
          <h2
            className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4"
            dangerouslySetInnerHTML={{ __html: bookingTitle }}
          />
        </div>

        <QuoteBookingConfirmationOptions
          blockData={{ ...blockData, bookingBody, depositAmount, depositDueDate }}
          depositAmount={depositAmount}
          depositDueDate={depositDueDate}

          onBookingConfirmed={() => setBookingConfirmed(true)}
        />
      </div>
    </section>
  );
}
